import React from 'react';

const TemplateFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  formData,
  validationErrors,
  formSubmitting,
  submitError,
  onFormChange,
  onVariableChange,
  onAddVariable,
  onRemoveVariable,
  onAddCtaButton,
  onRemoveCtaButton,
  onCtaButtonChange
}) => {
  if (!isOpen) return null;

  return (
    <div
      className={`template-modal-overlay ${isOpen ? "visible" : ""}`}
    >
      <div 
        className="template-modal-container template-form-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="template-modal-header">
          <h3 className="template-modal-title">{title}</h3>
          <button
            className="template-modal-close"
            onClick={onClose}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <form onSubmit={onSubmit}>
          <div className="template-modal-body">
            {submitError && (
              <div className="template-form-submit-error" role="alert">
                {submitError}
              </div>
            )}
            <div className="template-form-grid">
              <div className="template-form-group">
                <label className="template-form-label" htmlFor="name">Template Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className={`form-control ${validationErrors.name ? 'error' : ''}`}
                  value={formData.name}
                  onChange={onFormChange}
                  required
                  readOnly={title !== "Create New Template"}
                  disabled={title !== "Create New Template"}
                />
                {validationErrors.name && (
                  <div className="template-form-error">{validationErrors.name}</div>
                )}
                <small className="template-form-help">Only lowercase letters, numbers, and underscores allowed</small>
              </div>
              
              <div className="template-form-group">
                <label className="template-form-label" htmlFor="category">Category *</label>
                <select
                  id="category"
                  name="category"
                  className={`form-select ${validationErrors.category ? 'error' : ''}`}
                  value={formData.category}
                  onChange={onFormChange}
                  required
                >
                  <option value="UTILITY">UTILITY</option>
                  <option value="MARKETING">MARKETING</option>
                  <option value="TRANSACTIONAL">TRANSACTIONAL</option>
                </select>
                {validationErrors.category && (
                  <div className="template-form-error">{validationErrors.category}</div>
                )}
              </div>
              
              <div className="template-form-group template-form-full">
                <label className="template-form-label" htmlFor="body">Template Body *</label>
                <textarea
                  id="body"
                  name="body"
                  className={`form-control ${validationErrors.body ? 'error' : ''}`}
                  value={formData.body}
                  onChange={onFormChange}
                  rows="5"
                  required
                ></textarea>
                {validationErrors.body && (
                  <div className="template-form-error">{validationErrors.body}</div>
                )}
                <small className="template-form-help">{`Use {{variable}} syntax for dynamic content`}</small>
              </div>

              <div className="template-form-group template-form-full">
                <label className="template-form-label" htmlFor="imageUrl">Image Link (Optional)</label>
                <input
                  type="text"
                  id="imageUrl"
                  name="imageUrl"
                  className={`form-control ${validationErrors.imageUrl ? 'error' : ''}`}
                  value={formData.imageUrl || ""}
                  onChange={onFormChange}
                  placeholder="https://example.com/{{1}}.jpg"
                />
                {validationErrors.imageUrl && (
                  <div className="template-form-error">{validationErrors.imageUrl}</div>
                )}
                <small className="template-form-help">{`Provide a public image URL. You can include {{variable}} in the path for dynamic content.`}</small>
              </div>
              
              <div className="template-form-group template-form-full">
                <label className="template-form-label">Variables</label>
                {formData.variables.map((variable, index) => (
                  <div key={index} className="template-variable-row">
                    <input
                      type="text"
                      placeholder="Variable name *"
                      className="form-control"
                      value={variable.name}
                      onChange={(e) => onVariableChange(index, 'name', e.target.value)}
                      required
                    />
                    <input
                      type="text"
                      placeholder="Sample value *"
                      className="form-control"
                      value={variable.sampleValue}
                      onChange={(e) => onVariableChange(index, 'sampleValue', e.target.value)}
                      required
                    />
                    <button 
                      type="button" 
                      className="template-variable-remove"
                      onClick={() => onRemoveVariable(index)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                ))}
                {validationErrors.variables && (
                  <div className="template-form-error">{validationErrors.variables}</div>
                )}
                <div className="text-end">
                  <button 
                    type="button"
                    className="bouncy-button small w-100"
                    onClick={onAddVariable}
                  >
                    <i className="fas fa-plus"></i> <span>Add Variable</span>
                  </button>
                </div>
                <small className="template-form-help">Add variables if your template uses dynamic content with {`{{variable}}`} syntax</small>
              </div>

              <div className="template-form-group template-form-full">
                <label className="template-form-label">CTA Buttons (Optional)</label>
                {formData.ctaButtons && formData.ctaButtons.map((btn, index) => (
                  <div key={index} className="template-variable-row">
                    <input
                      type="text"
                      placeholder="Button title (max 25 chars) *"
                      className="form-control"
                      value={btn.title}
                      onChange={(e) => onCtaButtonChange(index, 'title', e.target.value)}
                      maxLength={25}
                    />
                    <input
                      type="text"
                      placeholder="https://example.com/{{1}} *"
                      className="form-control"
                      value={btn.url}
                      onChange={(e) => onCtaButtonChange(index, 'url', e.target.value)}
                    />
                    <button
                      type="button"
                      className="template-variable-remove"
                      onClick={() => onRemoveCtaButton(index)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                ))}
                {validationErrors.ctaButtons && (
                  <div className="template-form-error">{validationErrors.ctaButtons}</div>
                )}
                {(!formData.ctaButtons || formData.ctaButtons.length < 2) && (
                  <div className="text-end">
                    <button
                      type="button"
                      className="bouncy-button small w-100"
                      onClick={onAddCtaButton}
                    >
                      <i className="fas fa-plus"></i> <span>Add URL Button</span>
                    </button>
                  </div>
                )}
                <small className="template-form-help">Add up to 2 URL buttons that open a link when tapped. URL can include {`{{1}}`} for dynamic paths.</small>
              </div>
            </div>
          </div>
          
          <div className="template-modal-footer">
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={formSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bouncy-button small"
              disabled={formSubmitting}
            >
              <span>
                {formSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {title === "Create New Template" ? "Creating..." : "Updating..."}
                  </>
                ) : (
                  title === "Create New Template" ? "Create Template" : "Update Template"
                )}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateFormModal; 
